import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Truck, Shield, Heart, Banknote } from 'lucide-react';
import { ProductCard } from '../components/products/ProductCard';
import { formatPrice } from '../lib/utils';

const API_URL = '';

// Resolve image URLs from the shared utility
import { resolveImageUrl } from '../lib/imageUrl';

// Fallback images if admin hasn't set images yet
const FALLBACK_IMAGES = {
  'pyjama': 'https://images.unsplash.com/photo-1594632047623-f825a96296f6?w=600&h=800&fit=crop',
  'sous-vetements': 'https://images.unsplash.com/photo-1594631775771-890a902ae42d?w=600&h=800&fit=crop',
  'ensembles': 'https://images.unsplash.com/photo-1594631756462-1cc44a5fa1d8?w=600&h=800&fit=crop',
  'nuisette': 'https://images.unsplash.com/photo-1585129357576-aa4da9a68970?w=600&h=800&fit=crop',
  'deshabille': 'https://images.unsplash.com/photo-1765614765973-504c422f3a22?w=600&h=800&fit=crop',
  'kimono': 'https://images.unsplash.com/photo-1664362223041-89805535a89c?w=600&h=800&fit=crop',
  'corset-body': 'https://images.unsplash.com/photo-1623609163747-e4586361ea46?w=600&h=800&fit=crop',
  'combishort': 'https://images.unsplash.com/photo-1618932260643-eee4a2f652a6?w=600&h=800&fit=crop',
  'sport': 'https://images.unsplash.com/photo-1518459031867-a89b944bffe4?w=600&h=800&fit=crop',
  'beaute': 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=600&h=800&fit=crop',
  'accessoires': 'https://images.unsplash.com/photo-1611085583191-a3b181a88401?w=600&h=800&fit=crop',
};

const TESTIMONIALS = [
  {
    id: 1,
    name: 'Amina B.',
    location: 'Alger',
    rating: 5,
    comment: 'Qualité exceptionnelle! La lingerie est vraiment confortable et élégante. Je recommande à 100%!',
  },
  {
    id: 2,
    name: 'Sarah M.',
    location: 'Oran',
    rating: 5,
    comment: 'Service client au top et livraison rapide. Les pyjamas sont d\'une douceur incroyable.',
  },
  {
    id: 3,
    name: 'Lina K.',
    location: 'Constantine',
    rating: 5,
    comment: 'J\'ai commandé pour mon mariage, l\'ensemble était parfait. Merci Bloom!',
  },
];

export default function HomePage() {
  const [newArrivals, setNewArrivals] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);
  const [promoProducts, setPromoProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [banners, setBanners] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [newRes, bestRes, promoRes, catRes, bannerRes] = await Promise.all([
          fetch(`${API_URL}/api/products?is_nouvel_arrivage=true&limit=8`),
          fetch(`${API_URL}/api/products/bestsellers?limit=8`),
          fetch(`${API_URL}/api/products?is_promotion=true&limit=8`),
          fetch(`${API_URL}/api/categories`),
          fetch(`${API_URL}/api/site-banners`)
        ]);
        
        if (newRes.ok) {
          const newData = await newRes.json();
          setNewArrivals(newData.products || []);
        }
        
        if (bestRes.ok) {
          const bestData = await bestRes.json();
          setBestsellers(bestData.products || []);
        }

        if (promoRes.ok) {
          const promoData = await promoRes.json();
          setPromoProducts(promoData.products || []);
        }

        if (catRes.ok) {
          const catData = await catRes.json();
          const displayCats = (catData.categories || [])
            .filter(c => c.id !== 'nouvel-arrivage' && c.id !== 'promotion')
            .map(c => ({
              ...c,
              image: resolveImageUrl(c.image) || FALLBACK_IMAGES[c.id] || ''
            }));
          setCategories(displayCats);
        }

        if (bannerRes.ok) {
          const bannerData = await bannerRes.json();
          setBanners(bannerData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section relative" data-testid="hero-section">
        <div className="absolute inset-0 bg-[#1A1514]">
          {resolveImageUrl(banners?.desktop_image) && (
            <img
              src={resolveImageUrl(banners?.desktop_image)}
              alt="Bloom Lingerie"
              className="w-full h-full object-cover hidden md:block"
            />
          )}
          {(resolveImageUrl(banners?.mobile_image) || resolveImageUrl(banners?.desktop_image)) && (
            <img
              src={resolveImageUrl(banners?.mobile_image) || resolveImageUrl(banners?.desktop_image)}
              alt="Bloom Lingerie"
              className="w-full h-full object-cover md:hidden"
            />
          )}
          <div className="hero-overlay absolute inset-0" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center min-h-[90vh]">
          <div className="max-w-xl text-white">
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4 animate-fadeIn">
              Bloom Lingerie
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6 animate-slideUp">
              {banners?.overlay_title || 'La lingerie que tu mérites.'}
            </h1>
            <p className="text-lg text-gray-200 mb-8 animate-slideUp" style={{ animationDelay: '0.1s' }}>
              {banners?.overlay_subtitle || "Qualité premium. Tailles S à XXXL. Livraison dans toute l'Algérie."}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-slideUp" style={{ animationDelay: '0.2s' }}>
              <Link 
                to={banners?.overlay_button_link || "/shop"}
                className="btn-primary px-8 py-4 text-center text-sm font-medium uppercase tracking-wider"
                data-testid="hero-cta"
              >
                {banners?.overlay_button_text || 'Découvrir la Collection'}
              </Link>
              <Link 
                to="/size-guide" 
                className="btn-secondary px-8 py-4 text-center text-sm font-medium uppercase tracking-wider"
                data-testid="hero-size-guide"
              >
                Mon Guide des Tailles
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ NOUVEL ARRIVAGE — Products Grid (hidden when empty/error) ═══ */}
      {(loading || newArrivals.length > 0) && (
        <section className="py-20" data-testid="new-arrivals-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-12">
              <div>
                <h2 className="font-serif text-3xl">Nouvel Arrivage</h2>
              </div>
              <Link 
                to="/shop?is_nouvel_arrivage=true" 
                className="hidden md:flex items-center gap-2 text-sm font-medium hover:text-[#D4AF37] transition-colors"
                data-testid="view-all-new"
              >
                Voir Tout <ArrowRight size={16} />
              </Link>
            </div>

            {loading ? (
              <div className="product-grid">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-product bg-[#F5F4F2] mb-4" />
                    <div className="h-4 bg-[#F5F4F2] w-3/4 mb-2" />
                    <div className="h-4 bg-[#F5F4F2] w-1/2" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="product-grid">
                {newArrivals.slice(0, 4).map((product) => (
                  <ProductCard key={product.product_id} product={product} />
                ))}
              </div>
            )}

            <div className="md:hidden text-center mt-8">
              <Link 
                to="/shop?is_nouvel_arrivage=true" 
                className="inline-flex items-center gap-2 text-sm font-medium hover:text-[#D4AF37] transition-colors"
              >
                Voir Tout <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ═══ 3. ACHETER PAR CATÉGORIE ═══ */}
      <section className="py-16 bg-white" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl text-center mb-12">Acheter par Catégorie</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.map((category) => (
              <Link 
                key={category.id}
                to={`/shop?category=${category.id}`}
                className="category-card relative aspect-[3/4] overflow-hidden group"
                data-testid={`category-${category.id}`}
              >
                <img 
                  src={category.image}
                  alt={category.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute inset-0 flex items-end p-4 z-10">
                  <h3 className="text-white font-serif text-lg drop-shadow-md">{category.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ Promise Section ═══ */}
      <section className="py-16 bg-[#FCFBF9]" data-testid="promise-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="p-8">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center border border-gold rounded-full">
                <Shield size={28} className="text-gold" />
              </div>
              <h3 className="font-serif text-xl mb-3">Qualité Premium</h3>
              <p className="text-sm text-[#6B6A68]">
                Des matières soigneusement sélectionnées pour un confort et une élégance sans compromis.
              </p>
            </div>
            <div className="p-8">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center border border-gold rounded-full">
                <Heart size={28} className="text-gold" />
              </div>
              <h3 className="font-serif text-xl mb-3">Tailles S à XXXL</h3>
              <p className="text-sm text-[#6B6A68]">
                Chaque femme mérite de se sentir belle. Des tailles inclusives pour toutes les morphologies.
              </p>
            </div>
            <div className="p-8">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center border border-gold rounded-full">
                <Truck size={28} className="text-gold" />
              </div>
              <h3 className="font-serif text-xl mb-3">Livraison 58 Wilayas</h3>
              <p className="text-sm text-[#6B6A68]">
                Nous livrons partout en Algérie. Gratuit à partir de 10 000 DA.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* COD Reassurance Banner */}
      <section className="bg-[#1A1514] py-5" data-testid="cod-reassurance-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center gap-4">
          <Banknote size={24} className="text-[#D4AF37] flex-shrink-0" />
          <p className="text-white text-sm md:text-base font-medium text-center">
            Paiement à la livraison disponible — <span className="text-[#D4AF37]" dir="rtl">الدفع عند الإستلام</span>
          </p>
        </div>
      </section>

      {/* ═══ Bestsellers ═══ */}
      <section className="py-20" data-testid="bestsellers-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-gold mb-2">Les Plus Aimés</p>
              <h2 className="font-serif text-3xl">Meilleures Ventes</h2>
            </div>
            <Link 
              to="/shop?is_bestseller=true" 
              className="hidden md:flex items-center gap-2 text-sm font-medium hover:text-[#D4AF37] transition-colors"
              data-testid="view-all-bestsellers"
            >
              Voir Tout <ArrowRight size={16} />
            </Link>
          </div>
          
          {loading ? (
            <div className="product-grid">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-product bg-[#F5F4F2] mb-4" />
                  <div className="h-4 bg-[#F5F4F2] w-3/4 mb-2" />
                  <div className="h-4 bg-[#F5F4F2] w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="product-grid">
              {bestsellers.slice(0, 4).map((product) => (
                <ProductCard key={product.product_id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Size Guide CTA */}
      <section className="py-16 bg-[#F4E3E1]" data-testid="size-guide-cta">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-serif text-3xl mb-4">Pas sûre de ta taille?</h2>
          <p className="text-[#6B6A68] mb-8">
            Notre guide des tailles t'aide à trouver la coupe parfaite. 
            Simple, rapide, et précis.
          </p>
          <Link 
            to="/size-guide" 
            className="btn-primary px-8 py-4 text-sm font-medium uppercase tracking-wider inline-block"
            data-testid="size-guide-cta-btn"
          >
            Consulter le Guide
          </Link>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-gold mb-2">Témoignages</p>
            <h2 className="font-serif text-3xl">Ce que disent nos clientes</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((testimonial) => (
              <div key={testimonial.id} className="p-8 bg-white" data-testid={`testimonial-${testimonial.id}`}>
                <div className="star-rating mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} className="star-filled fill-current" />
                  ))}
                </div>
                <p className="text-[#6B6A68] mb-6 leading-relaxed italic">
                  "{testimonial.comment}"
                </p>
                <div>
                  <p className="font-medium">{testimonial.name}</p>
                  <p className="text-xs text-[#6B6A68]">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PROMOTION — Last Section ═══ */}
      <section className="py-20 bg-[#1A1514] text-white" data-testid="promotion-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#C87979] mb-2">Offres Spéciales</p>
              <h2 className="font-serif text-3xl text-white">Promotions</h2>
            </div>
            <Link 
              to="/shop?is_promotion=true" 
              className="hidden md:flex items-center gap-2 text-sm font-medium text-[#D4AF37] hover:text-white transition-colors"
              data-testid="view-all-promos"
            >
              Voir Tout <ArrowRight size={16} />
            </Link>
          </div>
          
          {promoProducts.length > 0 ? (
            <div className="product-grid">
              {promoProducts.slice(0, 4).map((product) => (
                <ProductCard key={product.product_id} product={product} />
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-400 py-8">De nouvelles promotions arrivent bientôt...</p>
          )}

          <div className="md:hidden text-center mt-8">
            <Link 
              to="/shop?is_promotion=true" 
              className="inline-flex items-center gap-2 text-sm font-medium text-[#D4AF37] hover:text-white transition-colors"
            >
              Voir Toutes les Promotions <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="newsletter-section py-20" data-testid="newsletter-section">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="font-serif text-3xl mb-4">Reste connectée</h2>
          <p className="text-gray-400 mb-8">
            Inscris-toi et reçois 10% sur ta première commande, 
            plus nos nouveautés en avant-première.
          </p>
          <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Ton adresse email"
              className="flex-1 px-4 py-4 bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:border-gold outline-none"
              data-testid="newsletter-email"
            />
            <button 
              type="submit"
              className="px-8 py-4 bg-gold text-[#1A1514] font-medium text-sm uppercase tracking-wider hover:bg-[#B8972F] transition-colors"
              data-testid="newsletter-submit"
            >
              S'inscrire
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
